import React, { useState, useEffect } from 'react';
import api from '../utils/api';

function TasksPage() {
    const [tasks, setTasks] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [showComments, setShowComments] = useState(false);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    
    // Filters
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterPriority, setFilterPriority] = useState('all');
    const [filterAssignee, setFilterAssignee] = useState('all');
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        priority: 'Medium',
        status: 'To Do',
        assigned_to_user_id: '',
        due_date: ''
    });

    useEffect(() => {
        loadTasks();
        loadUsers();
    }, []);

    const loadTasks = async () => {
        try {
            const response = await api.getTasks();
            if (response.data.success) {
                setTasks(response.data.tasks);
            }
        } catch (error) {
            console.error('Error loading tasks:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadUsers = async () => {
        try {
            const response = await api.getUsers();
            if (response.data.success) {
                setUsers(response.data.users.filter(u => u.is_active));
            }
        } catch (error) {
            console.error('Error loading users:', error);
        }
    };

    const loadComments = async (taskId) => {
        try {
            const response = await api.get(`/tasks/${taskId}/comments`);
            if (response.data.success) {
                setComments(response.data.comments);
            }
        } catch (error) {
            console.error('Error loading comments:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (selectedTask) {
                // Update existing task
                await api.put(`/tasks/${selectedTask.id}`, formData);
            } else {
                // Create new task
                await api.createTask(formData);
            }
            setShowModal(false);
            resetForm();
            loadTasks();
        } catch (error) {
            alert('Error saving task: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;
        
        try {
            await api.post(`/tasks/${selectedTask.id}/comments`, { comment: newComment });
            setNewComment('');
            loadComments(selectedTask.id);
        } catch (error) {
            alert('Error adding comment');
        }
    };

    const handleDeleteTask = async (id) => {
        if (!window.confirm('Are you sure you want to delete this task?')) return;
        
        try {
            await api.delete(`/tasks/${id}`);
            loadTasks();
        } catch (error) {
            alert('Error deleting task');
        }
    };

    const editTask = (task) => {
        setSelectedTask(task);
        setFormData({
            title: task.title,
            description: task.description || '',
            priority: task.priority,
            status: task.status,
            assigned_to_user_id: task.assigned_to_user_id || '',
            due_date: task.due_date ? task.due_date.split('T')[0] : ''
        });
        setShowModal(true);
    };

    const viewComments = (task) => {
        setSelectedTask(task);
        setShowComments(true);
        loadComments(task.id);
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            priority: 'Medium',
            status: 'To Do',
            assigned_to_user_id: '',
            due_date: ''
        });
        setSelectedTask(null);
    };

    // Filter tasks
    const filteredTasks = tasks.filter(task => {
        if (filterStatus !== 'all' && task.status !== filterStatus) return false;
        if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
        if (filterAssignee !== 'all' && task.assigned_to_user_id !== parseInt(filterAssignee)) return false;
        return true;
    });

    // Group tasks by status
    const tasksByStatus = {
        'To Do': filteredTasks.filter(t => t.status === 'To Do'),
        'In Progress': filteredTasks.filter(t => t.status === 'In Progress'),
        'Completed': filteredTasks.filter(t => t.status === 'Completed')
    };

    const getUserName = (userId) => {
        const user = users.find(u => u.id === userId);
        return user ? user.full_name : 'Unassigned';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const getPriorityColor = (priority) => {
        const colors = {
            'Low': '#10b981',
            'Medium': '#f59e0b',
            'High': '#ef4444',
            'Urgent': '#dc2626'
        };
        return colors[priority] || '#6b7280';
    };

    if (loading) return <div className="loading">Loading...</div>;

    return (
        <div className="page">
            <div className="page-header">
                <h1>Tasks</h1>
                <button onClick={() => setShowModal(true)} className="btn-primary">
                    + Create Task
                </button>
            </div>

            {/* Filters */}
            <div style={{ 
                display: 'flex', 
                gap: '1rem', 
                marginBottom: '2rem',
                padding: '1rem',
                background: '#f9fafb',
                borderRadius: '8px'
            }}>
                <div>
                    <label style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.25rem', display: 'block' }}>Status</label>
                    <select 
                        value={filterStatus} 
                        onChange={(e) => setFilterStatus(e.target.value)}
                        style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                    >
                        <option value="all">All Status</option>
                        <option value="To Do">To Do</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                    </select>
                </div>
                
                <div>
                    <label style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.25rem', display: 'block' }}>Priority</label>
                    <select 
                        value={filterPriority} 
                        onChange={(e) => setFilterPriority(e.target.value)}
                        style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                    >
                        <option value="all">All Priorities</option>
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Urgent">Urgent</option>
                    </select>
                </div>
                
                <div>
                    <label style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.25rem', display: 'block' }}>Assignee</label>
                    <select 
                        value={filterAssignee} 
                        onChange={(e) => setFilterAssignee(e.target.value)}
                        style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                    >
                        <option value="all">All Users</option>
                        {users.map(user => (
                            <option key={user.id} value={user.id}>{user.full_name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Kanban Board */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
                {Object.entries(tasksByStatus).map(([status, statusTasks]) => (
                    <div key={status} style={{ background: '#f9fafb', padding: '1rem', borderRadius: '8px' }}>
                        <h3 style={{ marginBottom: '1rem', fontSize: '1.125rem' }}>
                            {status} ({statusTasks.length})
                        </h3>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {statusTasks.map(task => (
                                <div key={task.id} style={{
                                    background: 'white',
                                    padding: '1rem',
                                    borderRadius: '8px',
                                    border: '1px solid #e5e7eb',
                                    borderLeft: `4px solid ${getPriorityColor(task.priority)}`
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                                        <h4 style={{ fontSize: '0.875rem', fontWeight: '600', margin: 0 }}>
                                            #{task.id} {task.title}
                                        </h4>
                                        <span style={{
                                            fontSize: '0.75rem',
                                            padding: '0.25rem 0.5rem',
                                            borderRadius: '4px',
                                            background: getPriorityColor(task.priority),
                                            color: 'white',
                                            fontWeight: '500'
                                        }}>
                                            {task.priority}
                                        </span>
                                    </div>
                                    
                                    {task.description && (
                                        <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0.5rem 0' }}>
                                            {task.description}
                                        </p>
                                    )}
                                    
                                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.75rem' }}>
                                        <div>👤 {getUserName(task.assigned_to_user_id)}</div>
                                        {task.due_date && <div>📅 Due: {formatDate(task.due_date)}</div>}
                                        {task.completed_at && <div>✅ Completed: {formatDate(task.completed_at)}</div>}
                                    </div>
                                    
                                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                                        <button
                                            onClick={() => editTask(task)}
                                            className="btn-sm"
                                            style={{ fontSize: '0.75rem' }}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => viewComments(task)}
                                            className="btn-sm"
                                            style={{ fontSize: '0.75rem' }}
                                        >
                                            💬 Comments
                                        </button>
                                        <button
                                            onClick={() => handleDeleteTask(task.id)}
                                            className="btn-sm"
                                            style={{ fontSize: '0.75rem', background: '#ef4444', color: 'white' }}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Create/Edit Task Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
                    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <h2>{selectedTask ? 'Edit Task' : 'Create Task'}</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Title *</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    required
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    rows="3"
                                />
                            </div>
                            
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Priority</label>
                                    <select
                                        value={formData.priority}
                                        onChange={(e) => setFormData({...formData, priority: e.target.value})}
                                    >
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="High">High</option>
                                        <option value="Urgent">Urgent</option>
                                    </select>
                                </div>
                                
                                <div className="form-group">
                                    <label>Status</label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                                    >
                                        <option value="To Do">To Do</option>
                                        <option value="In Progress">In Progress</option>
                                        <option value="Completed">Completed</option>
                                    </select>
                                </div>
                            </div>
                            
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Assign To</label>
                                    <select
                                        value={formData.assigned_to_user_id}
                                        onChange={(e) => setFormData({...formData, assigned_to_user_id: e.target.value})}
                                    >
                                        <option value="">Unassigned</option>
                                        {users.map(user => (
                                            <option key={user.id} value={user.id}>{user.full_name}</option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div className="form-group">
                                    <label>Due Date</label>
                                    <input
                                        type="date"
                                        value={formData.due_date}
                                        onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                                    />
                                </div>
                            </div>
                            
                            <div className="form-actions">
                                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="btn-secondary">
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary">
                                    {selectedTask ? 'Update Task' : 'Create Task'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Comments Modal */}
            {showComments && selectedTask && (
                <div className="modal-overlay" onClick={() => { setShowComments(false); setComments([]); }}>
                    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <h2>Comments - Task #{selectedTask.id}</h2>
                        <h3 style={{ fontSize: '1rem', color: '#6b7280', marginBottom: '1rem' }}>{selectedTask.title}</h3>
                        
                        {/* Add Comment Form */}
                        <form onSubmit={handleAddComment} style={{ marginBottom: '1.5rem' }}>
                            <div className="form-group">
                                <textarea
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Add a comment..."
                                    rows="3"
                                    required
                                />
                            </div>
                            <button type="submit" className="btn-primary" style={{ width: '100%' }}>
                                Add Comment
                            </button>
                        </form>
                        
                        {/* Comments List */}
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {comments.length === 0 ? (
                                <p style={{ textAlign: 'center', color: '#6b7280' }}>No comments yet</p>
                            ) : (
                                comments.map(comment => (
                                    <div key={comment.id} style={{
                                        padding: '1rem',
                                        background: '#f9fafb',
                                        borderRadius: '8px',
                                        marginBottom: '0.75rem'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                            <strong style={{ fontSize: '0.875rem' }}>{comment.full_name}</strong>
                                            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                {formatDate(comment.created_at)}
                                            </span>
                                        </div>
                                        <p style={{ fontSize: '0.875rem', margin: 0 }}>{comment.comment}</p>
                                    </div>
                                ))
                            )}
                        </div>
                        
                        <div style={{ marginTop: '1.5rem' }}>
                            <button onClick={() => { setShowComments(false); setComments([]); }} className="btn-secondary" style={{ width: '100%' }}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TasksPage;